import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type Connection,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  listIvrFlows,
  getIvrFlow,
  createIvrFlow,
  updateIvrFlow,
} from "../api/ivr";

// IVR node kinds → color. Each node is a labelled box; edges are the call flow.
const NODE_KINDS: { kind: string; label: string; color: string }[] = [
  { kind: "start", label: "Start (Answer)", color: "#16a34a" },
  { kind: "play", label: "Play Message", color: "#2563eb" },
  { kind: "menu", label: "Menu (Gather)", color: "#7c3aed" },
  { kind: "transfer", label: "Transfer", color: "#d97706" },
  { kind: "voicemail", label: "Voicemail", color: "#0891b2" },
  { kind: "hangup", label: "Hangup", color: "#dc2626" },
];

let idSeq = 1;
function nextId(): string {
  return `n${Date.now()}_${idSeq++}`;
}

const initialNodes: Node[] = [
  {
    id: "start",
    type: "input",
    position: { x: 250, y: 20 },
    data: { label: "Start (Answer)", kind: "start" },
    style: { borderColor: "#16a34a", borderWidth: 2 },
  },
];

export function IvrDesignerPage() {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [flowId, setFlowId] = useState<number | null>(null);
  const [name, setName] = useState("New IVR Flow");
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: flows, refetch } = useQuery({
    queryKey: ["ivr-flows"],
    queryFn: listIvrFlows,
  });

  const onConnect = useCallback(
    (conn: Connection) => setEdges((eds) => addEdge({ ...conn, animated: true }, eds)),
    [setEdges],
  );

  function addNode(kind: string, label: string, color: string) {
    const id = nextId();
    setNodes((nds) => [
      ...nds,
      {
        id,
        position: { x: 120 + Math.random() * 240, y: 120 + Math.random() * 240 },
        data: { label, kind },
        style: { borderColor: color, borderWidth: 2 },
        ...(kind === "hangup" ? { type: "output" } : {}),
      },
    ]);
  }

  async function loadFlow(id: number) {
    try {
      const flow = await getIvrFlow(id);
      setFlowId(flow.ivr_flow_id);
      setName(flow.name);
      setNodes(flow.definition.nodes?.length ? flow.definition.nodes : initialNodes);
      setEdges(flow.definition.edges ?? []);
      setSaveMsg(null);
    } catch (e) {
      setSaveMsg(`Load failed: ${(e as Error).message}`);
    }
  }

  async function save() {
    setSaving(true);
    setSaveMsg(null);
    try {
      const body = { name, definition: { nodes, edges } };
      if (flowId === null) {
        const id = await createIvrFlow(body);
        setFlowId(id);
      } else {
        await updateIvrFlow(flowId, body);
      }
      setSaveMsg("Saved ✓");
      await refetch();
    } catch (e) {
      setSaveMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  function newFlow() {
    setFlowId(null);
    setName("New IVR Flow");
    setNodes(initialNodes);
    setEdges([]);
    setSaveMsg(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h1 className="text-2xl font-bold">IVR Designer</h1>
        <div className="flex items-center gap-2">
          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={flowId ?? ""}
            onChange={(e) => (e.target.value ? loadFlow(Number(e.target.value)) : newFlow())}
          >
            <option value="">— New flow —</option>
            {(flows ?? []).map((f) => (
              <option key={f.ivr_flow_id} value={f.ivr_flow_id}>
                {f.name}
              </option>
            ))}
          </select>
          <input
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Flow name"
          />
          <button
            onClick={save}
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-1.5 text-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : flowId ? "Save" : "Create"}
          </button>
          {saveMsg && <span className="text-sm text-gray-600">{saveMsg}</span>}
        </div>
      </div>

      <div className="flex gap-3">
        <div className="w-44 shrink-0 space-y-2">
          <div className="text-xs uppercase text-gray-400 font-semibold">Add node</div>
          {NODE_KINDS.filter((n) => n.kind !== "start").map((n) => (
            <button
              key={n.kind}
              onClick={() => addNode(n.kind, n.label, n.color)}
              className="w-full text-left text-sm border rounded px-3 py-2 hover:bg-gray-50"
              style={{ borderLeft: `4px solid ${n.color}` }}
            >
              {n.label}
            </button>
          ))}
          <p className="text-xs text-gray-400 pt-2">
            Drag from a node's handle to another to connect the call flow.
          </p>
        </div>

        <div className="flex-1 bg-white rounded-lg shadow" style={{ height: "70vh" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
}
